import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ProductAddForm from '@/polymet/components/product-add-form';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';

export default function SupplierProductAddPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkAuthorization = async () => {
      setLoading(true);
      
      if (!user) {
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      try {
        // Check if the user is a supplier
        const { data: profile, error: profileError } = await supabase
          .from('accounts')
          .select('user_type')
          .eq('id', user.id)
          .single();
        
        if (profileError) throw profileError;
        
        // Only suppliers or users with both roles can add products
        setIsAuthorized(profile?.user_type === 'supplier' || profile?.user_type === 'both');
      } catch (error) {
        console.error('Error checking authorization:', error);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthorization();
  }, [user]);

  // Redirect unauthorized users
  useEffect(() => {
    if (!loading && !isAuthorized && !user) {
      navigate('/login?redirect=/supplier/products/add');
    }
  }, [loading, isAuthorized, user, navigate]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized && user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You must be a registered supplier to add products. Please contact support if you believe this is an error.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/supplier/dashboard')}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Package className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Add New Product</h1>
            <p className="text-muted-foreground">
              Create a new product listing for your inventory
            </p>
          </div>
        </div>
      </div>

      {/* Product Form */}
      <Card>
        <CardHeader>
          <CardTitle>Product Information</CardTitle>
          <CardDescription>
            Fill in the details below to create your product listing. All fields marked with * are required.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ProductAddForm />
        </CardContent>
      </Card>
    </div>
  );
} 